import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { getMicrophones, selectMicrophone } from "@/components/dialer/util/MicrophoneUtil";
import { useCallDialog } from "@/context/CallDialogContext";

interface ActiveCallSettingsProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ActiveCallSettings: React.FC<ActiveCallSettingsProps> = ({ isOpen, setIsOpen }) => {
  const { device } = useCallDialog();
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>("");

  const handleMicrophoneChange = async (deviceId: string) => {
    if (!device) {
      return;
    }
    try {
      await selectMicrophone(device, deviceId);
      setSelectedMicrophone(deviceId);
    } catch {
      toast.error("Could not switch to that microphone");
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const fetchMicrophones = async () => {
      const mics = await getMicrophones();
      if (!mics) {
        return;
      }
      setMicrophones(mics);
      const currentMicrophoneId = device?.audio?.inputDevice?.deviceId;
      const currentMicrophone = mics.find((mic) => mic.deviceId === currentMicrophoneId) ?? mics[0];
      setSelectedMicrophone(currentMicrophone?.deviceId ?? "");
    };
    fetchMicrophones();
  }, [isOpen, device]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Active Call Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {microphones.length > 0 ? (
            <div className="space-y-2">
              <Label htmlFor="microphone-select">Microphone</Label>
              <Select value={selectedMicrophone} onValueChange={handleMicrophoneChange}>
                <SelectTrigger id="microphone-select">
                  <SelectValue placeholder="Select a microphone" />
                </SelectTrigger>
                <SelectContent>
                  {microphones.map((mic) => (
                    <SelectItem key={mic.deviceId} value={mic.deviceId}>
                      {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}...`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No microphones available or permissions denied.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActiveCallSettings;
